import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Gauge, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { VehicleAPI, CustomerAPI, extractError } from "@/lib/api";
import { OdometerChart } from "@/components/OdometerChart";
import { VehicleDtcHistory } from "@/components/VehicleDtcHistory";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatReg } from "@/lib/india";
import { useFormatters, formatDateTime, formatDate } from "@/lib/format"; // eslint-disable-line no-unused-vars
// (`useFormatters` import is retained for the planned VehicleDetailPage tz refresh hook scaffolding in Phase 5.)

export default function VehicleDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { permissions } = useAuth();
    const canUpdate = permissions.includes("vehicle:update");
    const canDelete = permissions.includes("vehicle:delete");
    const canUpdateOdo = permissions.includes("vehicle:update_odometer");
    const [vehicle, setVehicle] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const v = await VehicleAPI.get(id);
            setVehicle(v);
            if (v.customer_id) {
                const c = await CustomerAPI.get(v.customer_id).catch(() => null);
                setCustomer(c);
            }
        } catch (e) {
            toast.error(extractError(e));
            if (e?.response?.status === 404) navigate("/vehicles");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function onDelete() {
        try { await VehicleAPI.remove(id); toast.success("Vehicle deleted"); navigate("/vehicles"); }
        catch (e) { toast.error(extractError(e)); }
    }

    if (loading) return <div className="text-sm text-zinc-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</div>;
    if (!vehicle) return null;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="vehicle-detail">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        {customer ? <Link to={`/customers/${customer.id}`} className="hover:underline">{customer.customer_code} · {customer.display_name}</Link> : "—"}
                    </div>
                    <h1 className="mt-1 plate font-display text-3xl font-bold tracking-tight sm:text-4xl">{formatReg(vehicle.registration_number)}</h1>
                    <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                        {vehicle.make} {vehicle.model} {vehicle.variant ? <span className="text-zinc-500">· {vehicle.variant}</span> : null} · {vehicle.year}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canUpdate && (
                        <Link to={`/vehicles/${id}/edit`}>
                            <Button variant="outline" data-testid="vehicle-edit-btn"><Pencil className="mr-1 h-4 w-4" /> Edit</Button>
                        </Link>
                    )}
                    {canUpdateOdo && <AddOdometer vehicle={vehicle} onSaved={load} />}
                    {canDelete && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="outline" className="text-rose-600" data-testid="vehicle-delete-btn"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete vehicle?</AlertDialogTitle>
                                    <AlertDialogDescription>Soft-deletes <span className="font-mono">{formatReg(vehicle.registration_number)}</span>.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction className="bg-rose-600 text-white hover:bg-rose-700" onClick={onDelete} data-testid="vehicle-delete-confirm">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
            </header>

            <Tabs defaultValue="overview">
                <TabsList>
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="odometer" data-testid="tab-odometer">Odometer</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
                    <TabsTrigger value="dtc-history" data-testid="tab-dtc-history">Diagnostics history</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <Card className="p-6">
                        <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                            <Row label="Fuel" value={vehicle.fuel_type} />
                            <Row label="Transmission" value={vehicle.transmission || "—"} />
                            <Row label="Color" value={vehicle.color || "—"} />
                            <Row label="VIN" value={vehicle.vin || "—"} mono />
                            <Row label="Engine" value={vehicle.engine_number || "—"} mono />
                            <Row label="Chassis" value={vehicle.chassis_number || "—"} mono />
                            <Row label="Odometer" value={`${(vehicle.odometer_current ?? 0).toLocaleString()} ${vehicle.odometer_unit || "km"}`} mono />
                        </dl>
                    </Card>
                </TabsContent>

                <TabsContent value="odometer">
                    <Card className="p-6">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="font-display text-lg font-bold tracking-tight">Odometer trend</h2>
                            {canUpdateOdo && <AddOdometer vehicle={vehicle} onSaved={load} />}
                        </div>
                        <OdometerChart entries={vehicle.odometer_history} unit={vehicle.odometer_unit} />
                        <div className="mt-4 rounded-md border border-zinc-200 dark:border-zinc-800">
                            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 text-sm">
                                {(vehicle.odometer_history || []).slice().reverse().map((e, i) => (
                                    <li key={i} className="flex items-center justify-between px-3 py-2 font-mono text-xs">
                                        <span>{formatDateTime(e.recorded_at)}</span>
                                        <span className="text-brand-redDeep dark:text-brand-red">{e.reading.toLocaleString()} {vehicle.odometer_unit || "km"}</span>
                                        <span className="text-zinc-500">{e.source}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="documents">
                    <Card className="p-6 space-y-4">
                        <Section title="Insurance" data={vehicle.insurance} />
                        <Section title="PUC (Pollution under control)" data={vehicle.puc} />
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <ServiceHistory vehicleId={id} />
                </TabsContent>

                <TabsContent value="dtc-history">
                    <VehicleDtcHistory vehicleId={id} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Section({ title, data }) {
    return (
        <div>
            <h3 className="mb-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{title}</h3>
            {!data ? (
                <div className="text-sm text-zinc-500">No data on file.</div>
            ) : (
                <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
                    {Object.entries(data).map(([k, v]) => (
                        <div key={k}>
                            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{k.replace(/_/g, " ")}</div>
                            <div className="font-mono text-sm">{v || "—"}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex flex-col gap-0.5">
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</dt>
            <dd className={mono ? "font-mono text-sm" : "text-sm"}>{value}</dd>
        </div>
    );
}

// ---------- Phase 4.1: real service history ----------

const HISTORY_STATUS_PILL = {
    booked: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    in_progress: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    awaiting_approval: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
    awaiting_parts: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
    qc: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200",
    ready: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    delivered: "bg-emerald-200 text-emerald-900 dark:bg-emerald-800/40 dark:text-emerald-100",
    cancelled: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
};

function formatDur(s) {
    if (s == null) return "—";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
}

function ServiceHistory({ vehicleId }) {
    const [state, setState] = useState({ loading: true, items: null, error: null });
    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, items: null, error: null });
        VehicleAPI.history(vehicleId)
            .then((d) => { if (!cancelled) setState({ loading: false, items: d.items || [], error: null }); })
            .catch((e) => { if (!cancelled) setState({ loading: false, items: [], error: extractError(e) }); });
        return () => { cancelled = true; };
    }, [vehicleId]);

    if (state.loading) {
        return (
            <Card className="grid h-32 place-items-center" data-testid="history-loading">
                <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
            </Card>
        );
    }
    if (state.error) {
        return (
            <Card className="p-6 text-sm text-rose-600" data-testid="history-error">{state.error}</Card>
        );
    }
    if (!state.items || state.items.length === 0) {
        return (
            <EmptyState
                title="No service history yet"
                description="When this vehicle goes through its first job card, it will show up here."
            />
        );
    }
    return (
        <Card className="overflow-hidden" data-testid="history-card">
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {state.items.map((row) => (
                    <li key={row.job_id}>
                        <Link
                            to={`/jobs/${row.job_id}`}
                            data-testid={`history-row-${row.job_id}`}
                            className="block px-4 py-3 transition-colors hover:bg-brand-redSoft/40 dark:hover:bg-brand-red/10"
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-mono text-sm font-semibold text-brand-redDeep dark:text-brand-red">{row.job_number}</span>
                                <span className={`rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${HISTORY_STATUS_PILL[row.status] || "bg-zinc-100 text-zinc-700"}`}>
                                    {(row.status || "").replace(/_/g, " ")}
                                </span>
                                <span className="ml-auto font-mono text-[11px] text-zinc-500">
                                    {row.opened_at ? formatDate(row.opened_at, { options: { day: "2-digit", month: "short", year: "numeric" } }) : "—"}
                                    {row.closed_at ? <> → <span className="text-zinc-700 dark:text-zinc-300">{formatDate(row.closed_at, { options: { day: "2-digit", month: "short", year: "numeric" } })}</span></> : null}
                                </span>
                            </div>
                            {row.summary && <div className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{row.summary}</div>}
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px] text-zinc-500">
                                {row.technician_names?.length > 0 && <span>Tech · {row.technician_names.join(", ")}</span>}
                                {row.odometer_in != null && <span>Odo in · {row.odometer_in.toLocaleString()} km</span>}
                                {row.odometer_out != null && <span>Odo out · {row.odometer_out.toLocaleString()} km</span>}
                                {row.total_tat_seconds != null && <span>TAT · {formatDur(row.total_tat_seconds)}</span>}
                                {row.attachment_count > 0 && <span>{row.attachment_count} photo{row.attachment_count === 1 ? "" : "s"}{row.has_before_after ? " (before/after)" : ""}</span>}
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>
        </Card>
    );
}

function AddOdometer({ vehicle, onSaved }) {
    const [open, setOpen] = useState(false);
    const [reading, setReading] = useState("");
    const [busy, setBusy] = useState(false);

    async function save() {
        setBusy(true);
        try {
            await VehicleAPI.addOdometer(vehicle.id, Number(reading));
            toast.success("Odometer reading recorded");
            setOpen(false);
            setReading("");
            onSaved && onSaved();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="odo-add-btn"><Plus className="mr-1 h-4 w-4" /> Add reading</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add odometer reading</DialogTitle>
                    <DialogDescription>Readings cannot regress below the last value ({vehicle.odometer_current?.toLocaleString()} {vehicle.odometer_unit || "km"}).</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Reading ({vehicle.odometer_unit || "km"})</label>
                    <div className="flex items-center gap-2">
                        <Gauge className="h-4 w-4 text-zinc-400" />
                        <Input type="number" min={vehicle.odometer_current ?? 0} value={reading} onChange={(e) => setReading(e.target.value)} className="font-mono tnum" data-testid="odo-input" autoFocus />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={save} disabled={busy || !reading} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="odo-save">
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
